import { getInitialLoadedFragmentCount } from "services";
import { LiveDelay, conditions, getUUID, highestResolutionSupported, videoTag } from "../../helpers";
import { ConditionName } from "../../interfaces";
import { sendFluentLog } from "../../logs/fluentdLogger";
import { initLog, loadingLog, pausePlayLog } from "../../logs/logs";
import {
    setActiveElements,
    setAutoQuality,
    setBufferedTimeWidth,
    setCanPlayVideo,
    setCurrentTime,
    setDuration,
    setFirstPlay,
    setHlsLivePosition,
    setIsEnded,
    setIsFullScreen,
    setIsLoading,
    setLoadedFragmentsCount,
    setManualQuality,
    setPlay,
    setQualityBitrates,
    setQualityLevels
} from "../slices/controllerSlice";
import { setClearPopoversSlice } from "../slices/popoversSlice";
import { setUserSettings } from "../slices/userSlice";
import {
    setIsComplexDesktop,
    setIsComplexPortrait,
    setIsLiveError,
    setMultiAudio,
    setSubtitleText,
    setSubtitles,
    setVideoTimelines
} from "../slices/videoDataSlice";
import store from "../store";
import { handleLockOrientation, handleVideoTimeline } from "./controller.actions";
import { createSettings } from "./user.actions";
import { getLiveAndVodDetails } from "./videoData.actions";
import { errorLogProvider } from "ui/src/components/modules/video-player-library/players/errorBoundaryFunctions";
import { handleContinuousPlayback } from "ui/src/playerComponents/continuousPlayback/continuousePlayback";

export const handleAutoFullScreen = () => {
    const dispatch = store.dispatch;
    const { params } = store.getState().videoData;
    const launchFullScreen = (element: any) => {
        if (element.requestFullScreen) element.requestFullScreen();
        else if (element.mozRequestFullScreen) element.mozRequestFullScreen();
        else if (element.webkitRequestFullScreen) element.webkitRequestFullScreen();
    };
    dispatch(setClearPopoversSlice());
    launchFullScreen(document.documentElement);
    dispatch(setIsFullScreen(params.fullscreen === "true"));
    if (conditions(ConditionName.isComplexDesktop)) dispatch(setIsComplexDesktop(false));
    if (conditions(ConditionName.isTouchScreen) && conditions(ConditionName.isParamsPortrait))
        dispatch(setIsComplexPortrait(params.fullscreen !== "true"));
};

export const appendQueryParamsToChunksIOS = () => {
    let fragmentExtension = ".ts";
    let originalOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function () {
        if (arguments[1].endsWith(fragmentExtension)) arguments[1] = arguments[1] + "?uuid=" + getUUID!();
        originalOpen.apply(this, arguments as any);
    };
};

export const handleBufferedTime = (event: any, intervalRef: any) => {
    const dispatch = store.dispatch;
    intervalRef.current = setInterval(() => {
        const bufferedTime = event.target.buffered.end(0);
        const bufferedTimeWidth = (bufferedTime / videoTag().duration) * 100;
        dispatch(setBufferedTimeWidth(bufferedTimeWidth));
    }, 500);
};

// ************* Hls Config Handlers ************* //
export const handleHlsConfig = () => {
    const { params, streamLinkParams } = store.getState().videoData;
    return {
        xhrSetup: (xhr: any, url: any) => {
            if (!url.split("m3u8")[1]) url = `${url}${streamLinkParams}`;
            xhr.open("GET", url, true);
        },
        startLevel: params.type === "live" ? 1 : -1,
        debug: false,
        enableWorker: true,
        liveDurationInfinity: conditions(ConditionName.hasQuizParams),
        maxLiveSyncPlaybackRate: conditions(ConditionName.hasQuizParams) ? 2 : 1,
        abrBandWidthUpFactor: conditions(ConditionName.hasQuizParams) ? 0.6 : 0.7,
        abrBandWidthFactor: conditions(ConditionName.hasQuizParams) ? 0.8 : 0.95,
        backBufferLength: conditions(ConditionName.hasQuizParams) ? 2 : Infinity,
        maxBufferLength: conditions(ConditionName.hasQuizParams) ? 12 : 30,
        maxMaxBufferLength: conditions(ConditionName.hasQuizParams) ? 600 : 60,
        liveSyncDuration: conditions(ConditionName.hasQuizParams) ? 3 : LiveDelay,
        liveMaxLatencyDuration: conditions(ConditionName.hasQuizParams) ? 8 : undefined,
        manifestLoadPolicy: {
            default: {
                maxTimeToFirstByteMs: Infinity,
                maxLoadTimeMs: 20000,
                timeoutRetry: {
                    maxNumRetry: 10,
                    retryDelayMs: 5000,
                    maxRetryDelayMs: 10000
                },
                errorRetry: {
                    maxNumRetry: 10,
                    retryDelayMs: 5000,
                    maxRetryDelayMs: 10000
                }
            }
        },
        fragLoadPolicy: {
            default: {
                maxTimeToFirstByteMs: 10000,
                maxLoadTimeMs: 120000,
                timeoutRetry: {
                    maxNumRetry: 10,
                    retryDelayMs: 5000,
                    maxRetryDelayMs: 10000
                },
                errorRetry: {
                    maxNumRetry: 10,
                    retryDelayMs: 5000,
                    maxRetryDelayMs: 10000
                }
            }
        },
        playlistLoadPolicy: {
            default: {
                maxTimeToFirstByteMs: 10000,
                maxLoadTimeMs: 20000,
                timeoutRetry: {
                    maxNumRetry: 10,
                    retryDelayMs: 5000,
                    maxRetryDelayMs: 10000
                },
                errorRetry: {
                    maxNumRetry: 10,
                    retryDelayMs: 5000,
                    maxRetryDelayMs: 10000
                }
            }
        }
    };
};

export const handleMultiLiveHlsConfig = () => {
    const { streamLinkParams } = store.getState().videoData;
    return {
        xhrSetup: (xhr: any, url: any) => {
            url = `${url}${streamLinkParams}`;
            xhr.open("GET", url, true);
        },
        startLevel: 1,
        debug: false,
        liveSyncDuration: LiveDelay,
        maxMaxBufferLength: 60,
        playlistLoadPolicy: {
            default: {
                maxTimeToFirstByteMs: 10000,
                maxLoadTimeMs: 20000,
                timeoutRetry: {
                    maxNumRetry: 1000,
                    retryDelayMs: 0,
                    maxRetryDelayMs: 0
                },
                errorRetry: {
                    maxNumRetry: 1000,
                    retryDelayMs: 1000,
                    maxRetryDelayMs: 1000
                }
            }
        }
    };
};

// ************* Manifest Handlers ************* //

export const handleManifestParsed = (hls: any, data: any) => {
    const dispatch = store.dispatch;
    let qualityLevels: any[] = [];
    let qualityBitrates: string[] = [""];
    let resolutions: any[] = [];
    hls.levels.map((level: any) => {
        qualityLevels.push(level.attrs.RESOLUTION!.split("x")[1].trim());
        qualityBitrates.push(`${(level.bitrate / 1000).toFixed(0)}kbps`);
        resolutions.push(level.attrs.RESOLUTION!);
    });
    hls.autoLevelCapping = highestResolutionSupported(resolutions, window.innerWidth, window.innerHeight);
    dispatch(setQualityLevels(qualityLevels));
    dispatch(setQualityBitrates(qualityBitrates));
    ["fullscreenchange", "webkitfullscreenchange", "mozfullscreenchange", "msfullscreenchange"].forEach((eventType) =>
        document.addEventListener(
            eventType,
            (event: any) => {
                if (conditions(ConditionName.isAutoQuality)) {
                    hls.autoLevelCapping = highestResolutionSupported(resolutions, window.innerWidth, window.innerHeight);
                    hls.nextLevel = -1;
                }
            },
            false
        )
    );
    if (data.audioTracks.length > 0) {
        const sortedAudioTracks = data.audioTracks.sort((a: any) => (a.default === true ? -1 : 1));
        dispatch(setMultiAudio(sortedAudioTracks));
    }
};

export const handleManifestLoaded = (hls: any) => (hls.audioTrack = 0);

export const handleMediaAttached = (Hls: any, hls: any) => {
    const { params, details } = store.getState().videoData;
    let streamLink = conditions(ConditionName.isGeneral) ? params.src! : details.stream_link;
    hls.loadSource(streamLink);
    hls.on(Hls.Events.MANIFEST_PARSED, (event: any, data: any) => handleManifestParsed(hls, data));
    hls.on(Hls.Events.MANIFEST_LOADED, () => handleManifestLoaded(hls));
};

// ************* Level Handlers ************* //

export const handleLevels = (hls: any, data: any, setQualityLevel: any) => {
    if (data.frag.type !== "audio") {
        const dispatch = store.dispatch;
        const { settings } = store.getState().user;
        const { qualityLevels, manualQuality, autoQuality } = store.getState().controller;
        if (!conditions(ConditionName.isAutoQuality)) {
            if (data.frag.level !== manualQuality) hls.nextLevel = qualityLevels[settings.quality!] ? settings.quality : -1;
            if (qualityLevels[settings.quality!]) {
                dispatch(setManualQuality(settings.quality!));
                dispatch(setAutoQuality(undefined));
            } else {
                setQualityLevel(qualityLevels[data.frag.level]);
                dispatch(setAutoQuality(data.frag.level));
                dispatch(setUserSettings({ ...settings, quality: -1 }));
                createSettings({ ...settings, quality: null });
            }
        } else if (conditions(ConditionName.isAutoQuality) && autoQuality === undefined) {
            hls.nextLevel = -1;
            setQualityLevel(qualityLevels[data.frag.level]);
            dispatch(setAutoQuality(data.frag.level));
        } else if (conditions(ConditionName.isAutoQuality)) {
            setQualityLevel(qualityLevels[data.frag.level]);
            if (autoQuality !== data.frag.level) dispatch(setAutoQuality(data.frag.level));
        }
    }
};

// ************* Fragment Handlers ************* //

export const handleFragBuffered = (hls: any) => {
    const dispatch = store.dispatch;
    if (store.getState().controller.loadedFragmentsCount.length < Number(getInitialLoadedFragmentCount()))
        dispatch(setLoadedFragmentsCount([...new Set([...store.getState().controller.loadedFragmentsCount, hls.abrController.fragCurrent.sn])]));
    const bufferedTime = hls.mainForwardBufferInfo?.end;
    const bufferedTimeWidth = (bufferedTime! / videoTag().duration) * 100;
    dispatch(setBufferedTimeWidth(bufferedTimeWidth));
};

export const handleFragLoading = (hls: any) => {
    const dispatch = store.dispatch;
    if (conditions(ConditionName.isLiveParam)) dispatch(setHlsLivePosition(hls.liveSyncPosition));
};

export const handleFragChanged = (Hls: any, hls: any, data: any, setQualityLevel: any) => {
    const { params, multiAudio, selectedAudioId } = store.getState().videoData;

    if (multiAudio && selectedAudioId !== undefined && selectedAudioId !== hls.audioTrack) {
        hls.audioTrack = selectedAudioId;
        hls.trigger(Hls.Events.AUDIO_TRACK_LOADING, multiAudio[selectedAudioId]);
    }
    if (params.type || !conditions(ConditionName.isLiveParam)) handleLevels(hls, data, setQualityLevel);
};

export const handleFragParsed = (hls: any, data: any, setQualityLevel: any) => {
    const { type } = store.getState().videoData.params;
    if (type || type === "live") handleLevels(hls, data, setQualityLevel);
};

// ************* HLS Error Handlers ************* //

export const handleHlsErrors = (Hls: any, data: any) => {
    if (data.event !== "hlsAudioTrackLoading") return { type: "HLS_" + data.type, message: data.error.message };
    return undefined;
};

export const handlePreview = (event: any) => {
    const dispatch = store.dispatch;
    const { params, details } = store.getState().videoData;
    const previewStart = conditions(ConditionName.hasPreviewStart) ? details.preview_start : 0;
    const previewEnd = conditions(ConditionName.hasPreviewEnd) ? details.preview_end : videoTag() ? videoTag().duration : 0;
    if (params.preview === "true" && event.target.currentTime >= previewEnd!) {
        videoTag().currentTime = previewStart!;
        dispatch(setCurrentTime(previewStart!));
    }
};

// ************* Video Tag Handlers ************* //

export const handleVideoWaiting = (event: any, waitingTimerRef: any) => {
    const dispatch = store.dispatch;
    event.preventDefault();
    waitingTimerRef.current = Date.now();
    dispatch(setIsLoading(true));
};

export const handleVideoSeeking = (event: any, waitingTimerRef: any) => {
    const dispatch = store.dispatch;
    event.preventDefault();
    waitingTimerRef.current = 0;
    dispatch(setIsLoading(true));
};

export const handleVideoPlay = (event: any, timerRef: any) => {
    const dispatch = store.dispatch;
    const { details, params } = store.getState().videoData;
    const { videoSettings } = store.getState().user;
    event.preventDefault();
    timerRef.current = Date.now();
    dispatch(setPlay(true));
    if (conditions(ConditionName.isFirstPlay)) {
        if (!conditions(ConditionName.isLiveParam)) {
            videoTag().currentTime = params.t ? +params.t : videoSettings.time;
            dispatch(setCurrentTime(params.t ? +params.t : videoSettings.time));
        }
        if (conditions(ConditionName.hasFullScreenParams)) handleAutoFullScreen();
        if (conditions(ConditionName.isFullScreen)) handleLockOrientation();
        if (conditions(ConditionName.hasPreviewParams)) {
            const previewStart = details.preview_start ? details.preview_start : 0;
            videoTag().currentTime = previewStart;
            dispatch(setCurrentTime(previewStart));
        }
        dispatch(setVideoTimelines([Math.floor(videoTag().currentTime)]));
    }
    sendFluentLog(pausePlayLog(true));
    dispatch(setSubtitleText(""));
};

export const handleVideoPlaying = (
    loadingCounterRef: any,
    loadingDurationRef: any,
    initialWaitingDurationRef: any,
    bannerLoadingDurationRef: any,
    timerRef: any,
    waitingDurationRef: any,
    waitingTimerRef: any,
    videoEntranceTimeRef: any
) => {
    const dispatch = store.dispatch;
    const { autoQuality, canPlayVideo, firstPlay, qualityLevels } = store.getState().controller;
    const { settings } = store.getState().user;
    const qualityLevel = settings.quality === -1 && autoQuality ? qualityLevels[autoQuality] : qualityLevels[settings.quality!];
    let waitingDuration = 0;
    if (!!waitingTimerRef.current) waitingDuration = Date.now() - waitingTimerRef.current;
    if (firstPlay && !!videoTag().played)
        sendFluentLog(
            initLog(
                loadingDurationRef.current,
                Date.now() - initialWaitingDurationRef.current,
                bannerLoadingDurationRef.current - initialWaitingDurationRef.current,
                Date.now() - timerRef.current,
                timerRef
            )
        );
    dispatch(setFirstPlay(false));
    if (canPlayVideo && waitingDuration > 0 && qualityLevel !== undefined) {
        waitingDurationRef.current = waitingDuration;
        waitingTimerRef.current = 0;
        sendFluentLog(loadingLog(waitingDuration, qualityLevel, timerRef, loadingCounterRef, videoEntranceTimeRef.current));
        loadingCounterRef.current = loadingCounterRef.current + 1;
        timerRef.current = Date.now();
    }
};

export const handleVideoPause = (event: any) => {
    const dispatch = store.dispatch;
    event.preventDefault();
    sendFluentLog(pausePlayLog(false));
    dispatch(setPlay(false));
};

export const handleVideoTimeUpdate = (event: any) => {
    const dispatch = store.dispatch;
    const { selectedSubtitle } = store.getState().videoData;
    event.preventDefault();
    handlePreview(event);
    dispatch(setCurrentTime(event.target.currentTime));
    if (selectedSubtitle && selectedSubtitle.activeCues && selectedSubtitle.activeCues.length > 0)
        dispatch(setSubtitleText(selectedSubtitle.activeCues[0].text));
};

export const handleVideoCanPlay = (event: any) => {
    const dispatch = store.dispatch;
    const { settings } = store.getState().user;
    event.preventDefault();
    dispatch(setIsLoading(false));
    dispatch(setCanPlayVideo(true));
    videoTag().volume = settings.volume / 100;
    if (!conditions(ConditionName.isIOSLive)) dispatch(setDuration(event.target.duration));
    if (conditions(ConditionName.isIOS)) {
        const subtitle: any = Object.values(event.target.textTracks);
        if (!!subtitle && subtitle.length) dispatch(setSubtitles(!!Object.values(subtitle[0]).length ? subtitle : []));
    } else dispatch(setSubtitles(Object.values(event.target.textTracks)));
};

export const handleVideoEnded = (event: any) => {
    const dispatch = store.dispatch;
    const { params } = store.getState().videoData;
    event.preventDefault();
    dispatch(setIsLiveError(true));
    dispatch(setIsEnded(true));
    dispatch(setActiveElements(true));
    handleVideoTimeline(videoTag().duration);
    if (conditions(ConditionName.hasOnFinishedParams)) params.onFinished!();
};

export const handleVideoError = (event: any) => {
    event.preventDefault();
    // Access the error property from the video element
    let error = videoTag().error;
    if (error) {
        switch (error.code) {
            case error.MEDIA_ERR_ABORTED:
                errorLogProvider({ type: "videoTagError", message: "You aborted the video playback." });
                break;
            case error.MEDIA_ERR_NETWORK:
                errorLogProvider({ type: "videoTagError", message: "A network error caused the video download to fail part-way." });
                break;
            case error.MEDIA_ERR_DECODE:
                errorLogProvider({
                    type: "videoTagError",
                    message:
                        "The video playback was aborted due to a corruption problem or because the video used features your browser did not support."
                });
                break;
            case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
                errorLogProvider({
                    type: "videoTagError",
                    message: "The video could not be loaded, either because the server or network failed or because the format is not supported."
                });
                break;
            default:
                errorLogProvider({ type: "videoTagError", message: "An unknown error occurred." });
                break;
        }
    } else {
        errorLogProvider({ type: "videoTagError", message: "An error occurred, but the error object is not available." });
    }
};

// ************* IOS Video Tag Handlers ************* //

export const handleIosVideoPlay = (event: any, timerRef: any) => {
    const dispatch = store.dispatch;
    event.preventDefault();
    timerRef.current = Date.now();
    dispatch(setFirstPlay(false));
};

export const handleIosVideoPlaying = () => {
    const dispatch = store.dispatch;
    dispatch(setIsLoading(false));
};

export const handleIosVideoTimeUpdate = (event: any) => {
    const dispatch = store.dispatch;
    event.preventDefault();
    dispatch(setCurrentTime(event.target.currentTime));
    handlePreview(event);
};

export const handleIosVideoEnded = () => {
    const { params } = store.getState().videoData;
    if (conditions(ConditionName.hasOnFinishedParams)) params.onFinished!();
};

// ************* Static Video Tag Handlers ************* //

export const handleStaticVideoPause = (event: any) => {
    const dispatch = store.dispatch;
    event.preventDefault();
    dispatch(setPlay(false));
};

export const handleStaticVideoPlay = (event: any, timerRef: any) => {
    const dispatch = store.dispatch;
    event.preventDefault();
    dispatch(setPlay(true));
    timerRef.current = Date.now();
    if (conditions(ConditionName.isFirstPlay) && conditions(ConditionName.hasFullScreenParams)) handleAutoFullScreen();
    if (conditions(ConditionName.isFullScreen)) handleLockOrientation();
};

export const handleStaticVideoPlaying = (loadingDurationRef: any, initialWaitingDurationRef: any, timerRef: any) => {
    const dispatch = store.dispatch;
    const { firstPlay } = store.getState().controller;
    dispatch(setIsLoading(false));
    if (firstPlay && !!videoTag().played)
        sendFluentLog(
            initLog(
                Math.round(loadingDurationRef.current),
                Date.now() - initialWaitingDurationRef.current,
                0,
                Date.now() - timerRef.current,
                timerRef
            )
        );
    dispatch(setFirstPlay(false));
};

export const handleStaticVideoTimeUpdate = (event: any) => {
    const dispatch = store.dispatch;
    event.preventDefault();
    dispatch(setCurrentTime(event.target.currentTime));
    dispatch(setDuration(event.target.duration));
};

export const handleStaticVideoCanPlay = (event: any) => {
    const dispatch = store.dispatch;
    event.preventDefault();
    dispatch(setIsLoading(false));
    dispatch(setCanPlayVideo(true));
};

export const handleStaticVideoLoadedData = (event: any, intervalRef: any) => {
    event.preventDefault();
    handleBufferedTime(event, intervalRef);
};

export const handleStaticVideoEnded = (event: any) => {
    const { params } = store.getState().videoData;
    if (conditions(ConditionName.hasOnFinishedParams)) params.onFinished!();
    event.preventDefault();
};

// ************* Quiz Video Tag Handlers ************* //
export const handleQuizVideoTimeUpdate = (event: any) => {
    const dispatch = store.dispatch;
    event.preventDefault();
    dispatch(setCurrentTime(event.target.currentTime));
};

export const handleQuizVideoPlaying = (event: any) => {
    const dispatch = store.dispatch;
    event.preventDefault();
    dispatch(setFirstPlay(false));
};

// ************* MultiLive Video Tag Handlers ************* //
export const handleMultiLiveManifestParsed = (hls: any) =>
    (hls.nextLevel = hls.levels.findIndex((level: any) => level.attrs.RESOLUTION!.split("x")[1].trim() === "144"));

export const handleMultiLiveMediaAttached = async (Hls: any, hls: any) => {
    const { multiLives } = store.getState().videoData;
    const { stream_link } = await getLiveAndVodDetails(multiLives[0].user_id);
    hls.loadSource(stream_link);
    hls.on(Hls.Events.MANIFEST_PARSED, () => handleMultiLiveManifestParsed(hls));
};

export const handleMultiLiveFragLoading = async (hls: any) => {
    const dispatch = store.dispatch;
    if (conditions(ConditionName.isLiveParam)) dispatch(setHlsLivePosition(hls.liveSyncPosition));
};

export const handleVideoVisibilityChange = (hls: any) => {
    handleVideoTimeline(videoTag().currentTime);
    handleContinuousPlayback();
    if (conditions(ConditionName.isVisibilityChange)) {
        if (document.hidden) hls.stopLoad();
        else hls.startLoad();
    }
};
